import React from 'react';
import { getSentimentTheme } from '@utils/SentimentThemes';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';

// A reusable, themed preview block for tributes (modal body or standalone).
// Props:
// - tribute: the full tribute doc object
// - docId: firestore doc id string (used by Like/Favorite)
// - currentUid: logged-in user id (for Like/Favorite)
// - showReactions: boolean (show Like/Favorite inside the preview)

export default function ThemedTributePreview({
  tribute = {},
  docId,
  currentUid,
  showReactions = true,
}) {
  const theme = getSentimentTheme(tribute?.tribute);

  return (
    <div
      className='rounded overflow-hidden'
      style={{
        border: `1px solid ${theme.borderColor}`,
        background: theme.cardAccent,
      }}
    >
      {/* Header */}
      <div
        className='p-3 d-flex align-items-center'
        style={{
          background: theme.headerBg,
          color: theme.textColor,
        }}
      >
        <i className={`${theme.iconClass} me-2`} style={{ fontSize: 22 }} />
        <h4 className='mb-0'>{tribute?.mainTitle || 'Untitled'}</h4>
        {tribute?.tribute && (
          <span
            className='badge ms-3'
            style={{ backgroundColor: theme.badgeBg, color: theme.badgeText }}
          >
            {tribute.tribute}
          </span>
        )}
      </div>

      {/* Banner image */}
      {tribute?.image && (
        <div className='w-100'>
          <img
            src={tribute.image}
            alt={tribute?.mainTitle || 'Tribute image'}
            className='w-100'
            style={{ maxHeight: 280, objectFit: 'cover' }}
            onError={(e) => (e.currentTarget.src = '/images/default.jpg')}
          />
        </div>
      )}

      {/* Body */}
      <div className='p-3'>
        {tribute?.subTitle && (
          <p className='text-muted mb-2' style={{ fontWeight: 600 }}>
            {tribute.subTitle}
          </p>
        )}

        {tribute?.message && (
          <p className='mb-3' style={{ color: '#333' }}>
            {tribute.message}
          </p>
        )}

        {/* Chips row (optional fields you want to highlight) */}
        <div className='d-flex flex-wrap gap-2'>
          {tribute?.dog && (
            <span
              className='badge'
              style={{
                background: theme.chip.bg,
                color: theme.chip.color,
              }}
            >
              🐾 {tribute.dog}
            </span>
          )}
          {tribute?.createdDate && (
            <span
              className='badge'
              style={{
                background: theme.chip.bg,
                color: theme.chip.color,
              }}
            >
              {typeof tribute.createdDate?.toDate === 'function'
                ? tribute.createdDate.toDate().toLocaleString()
                : String(tribute.createdDate)}
            </span>
          )}
          {tribute?.location && (
            <span
              className='badge'
              style={{
                background: theme.chip.bg,
                color: theme.chip.color,
              }}
            >
              📍 {tribute.location}
            </span>
          )}
        </div>

        {/* Reactions inside preview */}
        {showReactions && docId && currentUid && (
          <div className='d-flex align-items-center gap-2 mt-3'>
            <LikeButton
              collectionPath='memorials'
              documentId={docId}
              userId={currentUid}
              styled
            />
            <FavoriteButton
              collectionPath='memorials'
              documentId={docId}
              userId={currentUid}
              styled
            />
          </div>
        )}
      </div>
    </div>
  );
}
